import type { Sanctuary, World } from './types';
import { DEPTH_BINS, REEF_ISLANDS, stratumFloorKm2, stratumIndex, type Reef, type ReefIsland } from './reef';
import { reefOf } from './data/sites';
import { held } from './held';
import { logVariance, herbivoreCv } from './monitoring';
import { stockStream } from './streams';
import { herbivoreKgKm2 } from './systems/coral';
import { epochDayOf } from './calendar';

/**
 * What is known of each management area (M274, `docs/STRATEGIC_PLAY.md`'s section 2): Maui Nui's four islands, each
 * in the reef's three depths, as NOAA's divers survey them (its strata). The player reads an area as its last count
 * tells it, never the model's own: each depth's live coral as NOAA's 2024 survey found it (the archive's counts and
 * their errors, `MAUI_NUI_REEF`) until a later survey of the conservancy's counts it again; an island's herbivores and
 * its coral's bleaching unknown until the conservancy counts them. A count is the model's value with the survey's
 * error drawn on it, as `monitoring.ts` draws a program's, its error NOAA's own at that depth scaled by the sites.
 */

export type AreaKind = 'cover' | 'herbivores' | 'bleached';

/**
 * One count of one island: its live coral cover at each depth (percent of the floor), its herbivores (kg a hectare of
 * hard bottom, one value) or its live coral's share bleached at each depth (percent); each with its sites and its
 * standard error (none where a depth had no site), by NOAA's divers or the conservancy's.
 */
export interface AreaCount {
  day: number;
  island: ReefIsland;
  kind: AreaKind;
  by: 'noaa' | 'conservancy' | 'community';
  sites: number[];
  value: (number | null)[];
  se: (number | null)[];
}

/** The area counts kept, newest last (JUDGED: five years of a survey a month, more than any page reads). */
export const AREA_COUNTS_KEPT = 60;

/** NOAA's last survey's sites in a stratum, and its coefficient of variation there (its standard error over its cover), or none. */
function noaaStratum(reef: Reef, k: number): { sites: number; cv: number | null; cover: number | null; se: number | null } {
  const st = reef.surveys.at(-1)?.strata[k];
  if (!st) return { sites: 0, cv: null, cover: null, se: null };
  const cv = st.coverPercent !== null && st.sePercent !== null && st.coverPercent > 0 ? st.sePercent / st.coverPercent : null;
  return { sites: st.sites, cv, cover: st.coverPercent, se: st.sePercent };
}

/**
 * A survey's sites laid over the strata it covers, each by its share of their floor (NOAA's own allocation, by area;
 * JUDGED: NOAA weighs its strata by their variance too, `docs/STRATEGIC_PLAY.md`), the largest remainders rounded up,
 * so they sum to the sites given.
 */
export function allocateSites(reef: Reef, islands: readonly ReefIsland[], sites: number): number[] {
  const floor = stratumFloorKm2(reef);
  const out = floor.map(() => 0);
  const ks = islands.flatMap((island) => DEPTH_BINS.map((_, b) => stratumIndex(REEF_ISLANDS.indexOf(island), b)));
  const all = ks.reduce((a, k) => a + (floor[k] as number), 0);
  if (all <= 0 || sites <= 0) return out;
  // A survey of more than one island first gives each of its strata with reef a site, the rest by area (M327, one reef:
  // a whole reef's figure needs every stratum counted, Kahoʻolawe's small reef had drawn none at 20 sites. JUDGED: a
  // stratified estimate's own need; NOAA's allocation by area and variance, above, kept for the rest). One island's
  // survey as before.
  const floored = islands.length > 1 ? ks.filter((k) => (floor[k] as number) > 0) : [];
  if (floored.length > 0 && sites >= floored.length) {
    for (const k of floored) out[k] = 1;
    const rest = allocateSitesBy(floor, ks, sites - floored.length);
    return out.map((n, k) => n + (rest[k] as number));
  }
  return allocateSitesBy(floor, ks, sites);
}

/** Sites laid over strata `ks` by their share of the floor, the largest remainders rounded up. */
function allocateSitesBy(floor: readonly number[], ks: readonly number[], sites: number): number[] {
  const out = floor.map(() => 0);
  const all = ks.reduce((a, k) => a + (floor[k] as number), 0);
  if (all <= 0 || sites <= 0) return out;
  const exact = ks.map((k) => (sites * (floor[k] as number)) / all);
  ks.forEach((k, i) => { out[k] = Math.floor(exact[i] as number); });
  let left = sites - ks.reduce((a, k) => a + (out[k] as number), 0);
  const order = ks.map((k, i) => ({ k, r: (exact[i] as number) - Math.floor(exact[i] as number) })).sort((a, b) => b.r - a.r || a.k - b.k);
  for (const { k } of order) { if (left <= 0) break; out[k] = (out[k] as number) + 1; left--; }
  return out;
}

/** A stratum's coefficient of variation on a number of sites: NOAA's there, the error of a mean falling as the square root of its sites. */
export function stratumCv(reef: Reef, k: number, sites: number): number | null {
  const n = noaaStratum(reef, k);
  return sites > 0 && n.cv !== null && n.sites > 0 ? n.cv * Math.sqrt(n.sites / sites) : null;
}

/** A value with an error drawn on it, log-normal, its mean the truth (as `monitoring.ts`'s); the truth itself where it is 0 or its error none. */
function drawn(truth: number, cv: number | null, z: number): { value: number; se: number | null } {
  if (cv === null || cv <= 0 || truth <= 0) return { value: held(Math.max(0, truth)), se: cv === null ? null : 0 };
  const sigma = Math.sqrt(logVariance(cv));
  const value = held(truth * Math.exp(sigma * z - (sigma * sigma) / 2));
  return { value, se: held(value * cv) };
}

const mauiNui = (world: World): Sanctuary | undefined => world.sanctuaries.find((s) => s.siteId === 'maui_nui');

/**
 * A survey's counts of the areas it covers: one for each island, its sites laid by `allocateSites`; drawn on the
 * stream given, by the survey's number and its island (each count's draw its own, so one area's moves no other's).
 */
export function areaCounts(world: World, kind: AreaKind, islands: readonly ReefIsland[], sites: number, salt: number, surveyId: number): AreaCount[] {
  const s = mauiNui(world);
  const reef = s ? reefOf(s) : null;
  if (!s?.coral || !reef) return [];
  const laid = allocateSites(reef, islands, sites);
  const day = epochDayOf(world);
  const herbivores = kind === 'herbivores' ? herbivoreKgKm2(s.fish, day) : undefined;
  if (kind === 'herbivores' && !herbivores) return [];
  // An island no site was laid on is not counted (review M274's: an empty count hid NOAA's).
  return islands.flatMap((island): AreaCount[] => {
    const i = REEF_ISLANDS.indexOf(island);
    const rng = stockStream(world, salt, `area.${kind}.${island}`, surveyId);
    const ks = DEPTH_BINS.map((_, b) => stratumIndex(i, b));
    const strataSites = ks.map((k) => laid[k] as number);
    if (strataSites.every((n) => n === 0)) return [];
    if (kind === 'herbivores') {
      const n = strataSites.reduce((a, b) => a + b, 0);
      const noaa = REEF_ISLANDS.reduce((a, _, j) => a + DEPTH_BINS.reduce((x, __, b) => x + noaaStratum(reef, stratumIndex(j, b)).sites, 0), 0);
      // NOAA's error at its own sites, scaled to ours as a mean's error goes (1/√sites). JUDGED: our sites taken as
      // varied as NOAA's, their spread unmeasured apart from NOAA's (review 32).
      const cv = n > 0 ? herbivoreCv() * Math.sqrt(noaa / n) : null;
      // kg a km² to kg a hectare, as divers' biomass is told.
      const d = n > 0 ? drawn((herbivores?.[i] as number) / 100, cv, rng.normal()) : null;
      return [{ day, island, kind, by: 'conservancy', sites: [n], value: [d ? d.value : null], se: [d ? d.se : null] }];
    }
    const out = ks.map((k, b) => {
      const z = rng.normal();
      if ((laid[k] as number) <= 0) return null;
      const truth = kind === 'cover' ? (s.coral?.coverPercent[k] as number) : (s.coral?.bleachedPercent[b] as number);
      // The bleached share's error taken as the cover's at its sites (JUDGED: both read from the same photographs).
      return drawn(truth, stratumCv(reef, k, laid[k] as number), z);
    });
    return [{ day, island, kind, by: 'conservancy', sites: strataSites, value: out.map((d) => (d ? d.value : null)), se: out.map((d) => (d ? d.se : null)) }];
  });
}

/**
 * A reef-wide cover from a survey's counts of the islands, percent, with its coefficient of variation: each sampled
 * stratum weighted by its floor, the strata independent (as `coverCv` weighs NOAA's), so a survey spread over the whole
 * sanctuary tells the reef-wide cover its strata tell; none where none was sampled.
 */
export function reefWideOf(reef: Reef, counts: readonly AreaCount[]): { value: number; cv: number | null } | null {
  const floor = stratumFloorKm2(reef);
  let all = 0;
  let mean = 0;
  let variance = 0;
  for (const c of counts) {
    if (c.kind !== 'cover') continue;
    c.value.forEach((v, b) => {
      if (v === null) return;
      const a = floor[stratumIndex(REEF_ISLANDS.indexOf(c.island), b)] as number;
      all += a;
      mean += a * v;
      variance += (a * (c.se[b] ?? 0)) ** 2;
    });
  }
  if (all <= 0) return null;
  return { value: held(mean / all), cv: mean > 0 ? held(Math.sqrt(variance) / mean) : null };
}

/** What NOAA's last survey found on an island, as an area count (the archive's: its day the survey's middle one). */
function noaaCount(reef: Reef, island: ReefIsland): AreaCount | null {
  const survey = reef.surveys.at(-1);
  if (!survey) return null;
  const i = REEF_ISLANDS.indexOf(island);
  const strata = DEPTH_BINS.map((_, b) => noaaStratum(reef, stratumIndex(i, b)));
  return { day: survey.medianEpochDay, island, kind: 'cover', by: 'noaa', sites: strata.map((s) => s.sites), value: strata.map((s) => s.cover), se: strata.map((s) => s.se) };
}

/** An island's latest count of a kind: the conservancy's newest, or for its cover NOAA's last survey; none where nothing has counted it. */
export function latestCount(world: World, island: ReefIsland, kind: AreaKind): AreaCount | null {
  const counts = world.operations.areaCounts;
  for (let i = counts.length - 1; i >= 0; i--) {
    const c = counts[i] as AreaCount;
    if (c.island === island && c.kind === kind) return c;
  }
  if (kind !== 'cover') return null;
  const s = mauiNui(world);
  const reef = s ? reefOf(s) : null;
  return reef ? noaaCount(reef, island) : null;
}

/** What is known of one depth of an island (one value for its herbivores): its value, error, sites, day and whose count. */
export interface DepthKnown {
  day: number;
  by: 'noaa' | 'conservancy' | 'community';
  sites: number;
  value: number;
  se: number | null;
}

/**
 * What is known of an island's depth: the newest count that counted it, or for its cover NOAA's last survey (review
 * M274's: a survey that laid no site at a depth leaves what was known there); none where nothing has counted it. With
 * `before`, the newest from before that day.
 */
/** The days a fuller count stands over the volunteers' later months (M277; JUDGED: a season, three months). */
const COMMUNITY_SEASON_DAYS = 90;
/** The volunteers' counts kept for each island, the newest and the one before it to compare (M277: kept apart from the surveys' `AREA_COUNTS_KEPT`, review M277's, their months pushed the surveys' counts out). */
export const COMMUNITY_COUNTS_KEPT = 2;

/** The counts kept: the surveys' newest `AREA_COUNTS_KEPT`, and each island's volunteers' newest `COMMUNITY_COUNTS_KEPT`, in their order (M277). */
export function keptCounts(counts: readonly AreaCount[]): AreaCount[] {
  const surveys = counts.filter((c) => c.by !== 'community').slice(-AREA_COUNTS_KEPT);
  const ours = new Set<AreaCount>(surveys);
  for (const island of REEF_ISLANDS) for (const c of counts.filter((x) => x.by === 'community' && x.island === island).slice(-COMMUNITY_COUNTS_KEPT)) ours.add(c);
  return counts.filter((c) => ours.has(c));
}

export function knownAt(world: World, island: ReefIsland, kind: AreaKind, bin: number, before = Infinity): DepthKnown | null {
  const counts = world.operations.areaCounts;
  for (let i = counts.length - 1; i >= 0; i--) {
    const c = counts[i] as AreaCount;
    const v = c.value[bin];
    if (c.island !== island || c.kind !== kind || c.day >= before || v === null || v === undefined) continue;
    // A volunteers' month told only where no fuller count of the season before it stands (review M277's: a 40-site
    // census replaced the next month by ten transects; JUDGED, a season's counts read as one, the fullest of them).
    let k = c;
    for (let j = i - 1; c.by === 'community' && j >= 0; j--) {
      const x = counts[j] as AreaCount;
      if (c.day - x.day > COMMUNITY_SEASON_DAYS) break;
      if (x.island === island && x.kind === kind && x.by !== 'community' && (x.sites[bin] ?? 0) > (c.sites[bin] ?? 0) && x.value[bin] !== null && x.value[bin] !== undefined) { k = x; break; }
    }
    return { day: k.day, by: k.by, sites: k.sites[bin] ?? 0, value: k.value[bin] as number, se: k.se[bin] ?? null };
  }
  if (kind !== 'cover') return null;
  const s = mauiNui(world);
  const reef = s ? reefOf(s) : null;
  const noaa = reef ? noaaCount(reef, island) : null;
  const v = noaa?.value[bin];
  return noaa && v !== null && v !== undefined && noaa.day < before ? { day: noaa.day, by: 'noaa', sites: noaa.sites[bin] ?? 0, value: v, se: noaa.se[bin] ?? null } : null;
}

/** A depth's relative error as known, its standard error over its value, or none. */
export const knownError = (k: DepthKnown | null): number | null => (k && k.se !== null && k.value > 0 ? k.se / k.value : null);

/**
 * What the record shows of an island as a concern (`docs/STRATEGIC_PLAY.md`'s diagnosis), each from counts, none
 * invented:
 *
 * - **decline**: a depth whose cover in NOAA's last survey lies below its 2015-2019 surveys' highest by more than two
 *   standard errors of the difference (the counts independent);
 * - **uncertain**: a depth whose latest count's error is half its value or more (JUDGED: a count that cannot tell 20%
 *   from 40% cannot tell a reef's direction);
 * - **exposed**: its share of its reef floor in the shallows, where the heat is least softened by depth (the model's
 *   cooling with depth, `binHeatFallC`), the highest of the islands.
 */
export interface AreaConcerns {
  decline: { bin: number; from: number; fromYear: number; to: number } | null;
  uncertain: { bin: number; cover: number; se: number } | null;
  shallowShare: number;
  mostExposed: boolean;
}

export function concernsOf(world: World, island: ReefIsland): AreaConcerns | null {
  const s = mauiNui(world);
  const reef = s ? reefOf(s) : null;
  if (!reef) return null;
  const i = REEF_ISLANDS.indexOf(island);
  const last = reef.surveys.at(-1);
  let decline: AreaConcerns['decline'] = null;
  if (last) {
    DEPTH_BINS.forEach((_, b) => {
      const now = last.strata[stratumIndex(i, b)];
      if (!now || now.coverPercent === null || now.sePercent === null) return;
      for (const sv of reef.surveys.slice(0, -1)) {
        const then = sv.strata[stratumIndex(i, b)];
        if (!then || then.coverPercent === null || then.sePercent === null) continue;
        const gap = then.coverPercent - now.coverPercent;
        if (gap > 2 * Math.hypot(then.sePercent, now.sePercent) && (!decline || gap > decline.from - decline.to)) decline = { bin: b, from: then.coverPercent, fromYear: sv.year, to: now.coverPercent };
      }
    });
  }
  let uncertain: AreaConcerns['uncertain'] = null;
  DEPTH_BINS.forEach((_, b) => {
    const k = knownAt(world, island, 'cover', b);
    const r = knownError(k);
    // JUDGED (M274): an error half the cover or more is told as uncertain, the point where the count's lower bound at one
    // error nears none.
    if (k && r !== null && r >= 0.5 && (!uncertain || r > uncertain.se / uncertain.cover)) uncertain = { bin: b, cover: k.value, se: k.se as number };
  });
  const share = (j: number): number => {
    const floor = stratumFloorKm2(reef);
    const ks = DEPTH_BINS.map((_, b) => floor[stratumIndex(j, b)] as number);
    const all = ks.reduce((a, x) => a + x, 0);
    return all > 0 ? (ks[0] as number) / all : 0;
  };
  const shallowShare = share(i);
  return { decline, uncertain, shallowShare, mostExposed: REEF_ISLANDS.every((_, j) => share(j) <= shallowShare) };
}
